import React, { useMemo } from 'react';

import useFormatPrice from '../../Hooks/use-format-price';
import { IntervalPrice, IntervalWrapper, TimeslotsWrapper } from './style';
import Timeslot from './timeslot';
import { TimeslotInterval } from './arrange-in-intervals';
import { DateTime } from 'luxon';
import { Text } from '../../SubComponents/Text';
import { formatDuration } from '../../Utils/utils';
import useTypeSafeLocalize from '../../Hooks/use-type-safe-localize';
import { ErrorLocalization, LocalizationCategory } from '../../localizations/types';
import { BookingLocalization } from '../../Utils/graphql-sdk';
import useLocale from '../../Hooks/use-locale';
import localizations from '../../localizations';
import { ParsedTimeslot } from './timeslots';
import { useGetCustomBookingLocalization } from '../../Hooks/use-get-custom-booking-localization';

// Might come from shop settings at some point
const DEFAULT_VACANCY_THRESHOLD = 2;

interface IntervalProps {
  interval: TimeslotInterval;
  bookingDate: Date | null;
  duration: number;
  onChange: (timeslot: ParsedTimeslot) => void;
  enabled: boolean;
  boatCount: number;
  vacancyThreshold: number | null | undefined;
  localizedTimeFormat: boolean;
  customerFacingText?: string | null;
}

const Interval = (props: IntervalProps) => {
  const {
    interval,
    bookingDate,
    duration,
    onChange,
    enabled,
    boatCount,
    vacancyThreshold,
  } = props;
  const formatPrice = useFormatPrice();
  const { languageAsEnum } = useLocale();
  const localization = localizations[languageAsEnum];
  const localize = useTypeSafeLocalize();
  const VACANCY_THRESHOLD = vacancyThreshold ?? DEFAULT_VACANCY_THRESHOLD;
  const getCustomBookingLocalization = useGetCustomBookingLocalization();

  const isBookable = useMemo(
    () => interval.timeslots.some((timeslot) => timeslot.bookable),
    [interval.timeslots]
  );

  return (
    <IntervalWrapper>
      <IntervalPrice>
        {interval.isBlockedByMinimumNotice
          ? getCustomBookingLocalization(
              BookingLocalization.NotAvailableForOnlineBooking
            ) ||
            localize(
              LocalizationCategory.error,
              ErrorLocalization.NotAvailableForOnlineBooking
            )
          : isBookable
          ? formatPrice(interval.price * boatCount)
          : localize(LocalizationCategory.error, ErrorLocalization.NotAvailable)}
      </IntervalPrice>

      <TimeslotsWrapper>
        {interval.timeslots.map((timeslot) => {
          const isDisabled = !enabled || !timeslot.bookable;
          const shouldWarn = !isDisabled && timeslot.vacancies <= VACANCY_THRESHOLD;
          const selected = bookingDate
            ? DateTime.fromJSDate(bookingDate).toMillis() ===
              timeslot.startTime.toMillis()
            : false;

          return (
            <Timeslot
              timeslot={timeslot}
              selected={selected}
              disabled={isDisabled}
              warning={shouldWarn}
              key={timeslot.startTime.toISO()}
              onChange={onChange}
              localizedTimeFormat={props.localizedTimeFormat}
            />
          );
        })}
      </TimeslotsWrapper>

      {isBookable &&
        !interval.isBlockedByMinimumNotice &&
        interval.customerFacingText && (
          <Text
            mt={13}
            dangerouslySetInnerHTML={{ __html: interval.customerFacingText }}
          />
        )}

      {!isBookable && !interval.isBlockedByMinimumNotice && (
        <Text mt={13}>
          {getCustomBookingLocalization(BookingLocalization.DurationTimesUnavailable) ||
            localize(
              LocalizationCategory.error,
              ErrorLocalization.DepartureTimesNotAvailable
            ) +
              ' ' +
              formatDuration(duration, { localization })}
        </Text>
      )}
    </IntervalWrapper>
  );
};

export default Interval;
